import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/**
 * Which table the guest is sitting at, for as long as this browser tab lives.
 *
 * Deliberately sessionStorage, not localStorage: a phone that ordered at table
 * 12 last week must not silently put tonight's order on table 12 again. The
 * scan is the act that binds a device to a table, and closing the tab ends it.
 */
export interface QrSession {
  tableId: string;
  tableReference: string;
  sectionName?: string;
  /** The scanned token, kept so a reload can re-resolve without a new scan. */
  token?: string;
}

interface QrSessionStore {
  session?: QrSession;
  setSession: (session: QrSession) => void;
  clearSession: () => void;
}

export const useQrSessionStore = create<QrSessionStore>()(
  persist(
    (set) => ({
      session: undefined,
      setSession: (session) => set({ session }),
      clearSession: () => set({ session: undefined }),
    }),
    {
      name: "qr-session",
      storage: createJSONStorage(() =>
        // The store is created during SSR too, where there is no window. The
        // no-op storage keeps the server render at "no session", which is the
        // correct starting point anyway.
        typeof window === "undefined"
          ? {
              getItem: () => null,
              setItem: () => undefined,
              removeItem: () => undefined,
            }
          : window.sessionStorage
      ),
    }
  )
);

/**
 * True once the guest has scanned (or typed) their table. Everything QR-specific
 * in the shared menu UI keys off this rather than off the route, because the
 * menu itself is the ordinary /menu route.
 */
export const useIsQrSession = () =>
  useQrSessionStore((state) => state.session !== undefined);
